import moment from "moment";

import { Client } from "@/Common/Api/Client";
import { Employee } from "@/Common/Api/Employee/Dto/Employee";
import { LicenceOrganisationRule } from "@/Common/Api/MedfarDictionaries/Dto/Responses/LicenceOrganisationRule";
import { Pharmacy } from "@/Common/Api/Pharmacy/Dto/Responses/Pharmacy";
import { AdditionalSignOffInformation } from "@/Common/Components/Modals/LinkingPopup/LinkingPopup";
import { EnumHelper } from "@/Common/Helpers/EnumHelper";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { IAllowlistModel } from "@/Common/Models/Administration/IAllowlistModel";
import { EnumAppointmentSource } from "@/Common/Models/Appointments/EnumAppointmentSource";
import { PatientAppointmentItem } from "@/Common/Models/Appointments/PatientAppointmentItem";
import { MedicalNotePinOption } from "@/Common/Models/Employee/Enums/MedicalNotePinOption";
import { FaxLabRequestComposite } from "@/Common/Models/Faxing/FaxLabRequestComposite";
import { Guid } from "@/Common/Models/Guid";
import { GenderIdentity } from "@/Common/Models/Individuals/Enums/GenderIdentity";
import { PreferredPronouns } from "@/Common/Models/Individuals/Enums/PreferredPronouns";
import { Patient } from "@/Common/Models/Patient/Patient";
import { Gender, GrowthChartStandard, UnitSystem } from "@/Common/Models/Patient/PatientEnums";
import { PatientExternalIdentifier } from "@/Common/Models/Patient/PatientExternalIdentifier";
import { PatientRefIntervenorWithDetailsModel } from "@/Common/Models/Patient/PatientRefIntervenorWithDetailsModel";
import { PortalLinkedItemType } from "@/Common/Models/Portal/Enums/PortalLinkedItemType";
import { SharedItem } from "@/Common/Models/Portal/SharedItem/SharedItem";
import { SitesModel } from "@/Common/Models/Sites/SitesModel";
import { PatientBillingHistoryOrderType, PrivateBill_BilledTo, SortDirection } from "@/Common/Models/UI/Enums";
import { integrationBus } from "@/Common/Modularity/IntegrationBus/IntegrationBus";
import * as ServerOperations from "@/Common/Operations/operations.server";
import * as SubServerOperations from "@/Common/Operations/operations.server.sub";
import { CanadaState } from "@/Common/Resources/Lookups/Enums/CommonLookupCodes";
import { BaseAction, IRequestExecutionContext, IResponseExecutionContext } from "@/Common/Stores/Actions/BaseAction";
import { ExportableFile } from "@/ExportableFile/Models/ExportableFile";
import { FaxConsultationRequestComposite } from "@/ExportableFile/Models/FaxConsultationRequestComposite";
import { UserSettings } from "@/Globals/Models/UserSettings";
import { GeneratePatientBillingHistoryPdfResponse } from "@/Patient/Api/Dto/Responses/MedicalNote/PatientBillingHistory/GenerateBillingHistoryPdfResponse";
import { LoadingEncountersCacheEvent } from "@/Patient/IntegrationBus/PatientEvents";
import { Encounter } from "@/Patient/Models/MedicalSummary/Encounter";
import { EncounterNote } from "@/Patient/Models/MedicalSummary/EncounterNote";
import { MedicalItemProblemAndCondition } from "@/Patient/Models/MedicalSummary/MedicalItemProblemAndCondition";
import { ObstetricalFileContributionType } from "@/Patient/Models/ObstetricalFile/Enum/ObstetricalFileContributionType";
import { ObstetricalFileType } from "@/Patient/Models/ObstetricalFile/Enum/ObstetricalFileType";
import { IBillableService } from "@/Patient/Models/UI/History/IBillableService";
import { IPatientPrivateBillModel } from "@/Patient/Models/UI/History/IPatientPrivateBillModel";
import { IPatientMedicalNoteModel } from "@/Patient/Models/UI/MedicalNote/IPatientMedicalNoteModel";
import { IPatientMedicalSummaryModel } from "@/Patient/Models/UI/MedicalSummary/IPatientMedicalSummaryModel";
import { IMyle_Patient_MedicalNote_GetFormPersistRequest, IMyle_Patient_MedicalNote_GetStatusRequest, IMyle_Patient_MedicalNote_GetUpdateConsultationRequest } from "@/Patient/Stores/Handlers/Server/PatientMedicalNoteResponseHandler";

class PatientMedicalNoteActions extends BaseAction {
    private readonly helper = new MyleHelper();

    constructor(onBeforeExecute?: IRequestExecutionContext, onBeforeHandleResponse?: IResponseExecutionContext) {
        super(onBeforeExecute, onBeforeHandleResponse);
    }

    public ReloadApssForms(encounterId: string): void {
        const requestData =
        {
            IncludeApssForms: true,
            EncounterIds: [encounterId]
        };

        this.Execute(ServerOperations.Server.Myle_GetPatientForms, requestData);
    }

    public UpdateConsultationDetails(
        encounterId: Guid,
        consultationDate: Date,
        consultationType: string,
        contextType: string,
        consultationSiteId: Guid,
        consultationDepartmentId: Guid,
        isActiveSite: boolean,
        isActiveDepartment: boolean,
        appointmentId: string,
        enableAppointmentLinkSuggestions: boolean
    ): void {
        let requestData: IMyle_Patient_MedicalNote_GetUpdateConsultationRequest =
        {
            EncounterId: encounterId,
            ConsultationDate: consultationDate,
            ConsultationTypeLookupId: consultationType,
            ConsultationContextLookupId: contextType,
            ConsultationSiteId: consultationSiteId,
            ConsultationDepartmentId: consultationDepartmentId,
            SetConsultationSiteAsActive: isActiveSite,
            SetConsultationDepartmentAsActive: isActiveDepartment,
            AppointmentId: appointmentId,
            EnableAppointmentLinkSuggestions: enableAppointmentLinkSuggestions
        };

        if (!!cacheStore.CurrentUserDefaultSpecializationLookupId && cacheStore.CurrentUserDefaultSpecializationLookupId != GuidHelper.Empty())
            cacheStore.CurrentUserDefaultSpecializationLookupId = consultationType;

        this.Execute(ServerOperations.Server.Myle_Patient_MedicalNote_UpdateConsultationDetails, requestData, null, null, null, null, () => {
            portfolioStore.PortfolioActions.LoadPortfolioCount();
        });
    }

    public LoadEncounterWithoutOpeningView(encounterId: string): void {
        patientStore.IsLoading = true;

        if (encounterId) {
            const requestData = {
                EncounterId: encounterId
            };

            this.Execute(ServerOperations.Server.Myle_Patient_MedicalNote_LoadEncounter, requestData, SubServerOperations.MedicalNote.ForGrowthChart);
        }
    }

    /**
     * Save user settings to the backend and update the cache store.
     * @param settings The settings to update. Only non-null fields will be updated.
     * @param onError A callback invoked if the request fails.
     */
    public saveUserSettings(settings: Partial<UserSettings>, onError?: () => void): void {
        this.Execute(
            ServerOperations.Server.Myle_UpdateUserSettings,
            {
                UserSettings: settings,
                UserId: sessionStore.userId
            },
            null,
            null,
            () => onError?.(),
            null,
            () => cacheStore.UserSettings = { ...cacheStore.UserSettings, ...settings }
        );
    }

    public LoadEncounterForEdit(encounterId: string, openPatientTab: boolean = false): void {
        patientStore.IsLoading = true;

        if (encounterId && encounterId.length >= 0) {
            const requestData = {
                EncounterId: encounterId
            };
            const subServerOperation = openPatientTab ? SubServerOperations.MedicalNote.LoadEncounterForEditAndRedirectToPatientTab : SubServerOperations.MedicalNote.LoadEncounterForEdit;

            this.Execute(ServerOperations.Server.Myle_Patient_MedicalNote_LoadEncounter, requestData, subServerOperation);
        }
    }

    public async LoadEncountersCache(encounterIds: string[], currentPatientId: string): Promise<void> {
        if (encounterIds?.any()) {
            const requestData = {
                EncounterIds: encounterIds,
                CurrentPatientId: currentPatientId ?? null
            };

            await this.ExecuteAsync(ServerOperations.Server.Myle_Patient_MedicalNote_LoadEncountersById, requestData);
            integrationBus.emit(new LoadingEncountersCacheEvent({ encounterIds: encounterIds as Guid[] }));
        }
    }

    public LoadEncounterForView(encounterId: string, currentPatientId: string = null, forAction: SubServerOperations.MedicalNote = null, includePdfFormTokens: boolean = false, onComplete?: () => void): void {
        if (typeof encounterId !== "undefined" && encounterId.length > 0) {
            const requestData = {
                EncounterId: encounterId,
                CurrentPatientId: currentPatientId,
                IncludePdfFormTokens: includePdfFormTokens
            };

            this.Execute(ServerOperations.Server.Myle_Patient_MedicalNote_LoadEncounter, requestData, !forAction ? SubServerOperations.MedicalNote.LoadEncounterForView : forAction, false, null, null, onComplete);
        }
    }

    public ManagePatientAppointmentState(patientContactId: string, encounterId: string, date: Date, eventType: AppointmentStatusChangeEventType): void {
        if (typeof patientContactId !== "undefined" && patientContactId.length > 0 && !cacheStore.ClinicSettings.IsCalendarReadOnly) {
            const requestData = {
                PatientContactId: patientContactId,
                EncounterDate: date,
                EncounterId: encounterId,
                EventType: eventType // modify encounter
            };

            this.Execute(ServerOperations.Server.Myle_Patient_MedicalNote_ManageAppointmentState, requestData);
        }
    }

    public LoadMedfarBillForEncounter(encounterId: string): void {
        const requestData = {
            EncounterIds: [encounterId]
        };

        this.Execute(ServerOperations.Server.MedfarBilling_GetBill, requestData);
    }

    public SaveEncounterNote(encounterId: Guid, noteText: string, noteContext: string, employeeContactFacetId: Guid): void {
        const note = new EncounterNote();
        note.Context = noteContext;
        note.DateAdded = this.helper.CreateDateAsUTC(moment().toDate());
        note.EmployeeId = employeeContactFacetId;
        note.EncounterId = encounterId;
        note.Note = noteText;

        const requestData = {
            EncounterId: encounterId,
            EncounterNote: note
        };

        this.Execute(ServerOperations.Server.Myle_Patient_MedicalNote_AddEncounterNote, requestData);
    }

    public LoadPrivateBillInfo(billId: string): void {
        const requestData = {
            PrivateBillId: billId
        };

        this.Execute(ServerOperations.Server.Myle_Patient_History_GetSelectedBillInformation, requestData, SubServerOperations.PrivateBill.LoadInfoFromNote);
    }

    public LoadPrivateBillInfoForPrint(billId: string): void {
        const requestData = {
            PrivateBillId: billId
        };

        this.Execute(ServerOperations.Server.Myle_Patient_History_GetSelectedBillInformation, requestData, SubServerOperations.PrivateBill.LoadInfoFromNote_ForPrint);
    }

    public LoadPrivateBillInfoForReuse(privateBillId: string): void {
        const requestData = {
            PrivateBillId: privateBillId
        };

        this.Execute(ServerOperations.Server.Myle_Patient_History_GetSelectedBillInformation, requestData, SubServerOperations.PrivateBill.LoadInfoFromReusing);
    }

    public LoadPrivateBillInfoForEdit(privateBillId: string): void {
        const requestData = {
            PrivateBillId: privateBillId
        };

        this.Execute(ServerOperations.Server.Myle_Patient_History_GetSelectedBillInformation, requestData, SubServerOperations.PrivateBill.LoadInfoFromEdit);
    }

    public CreateNote(patientContactFacetId: string, siteId: string, specializationId: string, contextLookupId: string = null, costCenterId: string = null): void {
        if (contextLookupId == null) {
            contextLookupId = cacheStore.FindLookupByLookupCode("Clinic Visite", "CONSULTATIONCONTEXT").LookupID;
        }

        const requestData = {
            PatientContactFacetId: patientContactFacetId,
            SiteId: siteId,
            ConsultationTypeId: specializationId,
            ConsultationContextId: contextLookupId,
            CostCenterId: costCenterId
        };

        this.Execute(ServerOperations.Server.Myle_Patient_MedicalNote_CreateEncounter, requestData);
    }

    public SaveNoteActionButtonLog(encounterId: Guid, appointmentId: string, actionType: MedicalNotePinOption, isViewMode: boolean, employeeId: string): void {
        const requestData = {
            EmployeeId: employeeId,
            EncounterId: encounterId,
            AppointmentId: appointmentId,
            SelectedActionTypeId: actionType,
            IsViewMode: isViewMode
        };

        this.dispatch(ServerOperations.Server.Myle_StreamlineNoteCompletion_SaveNoteActionButtonLog, requestData);
    }

    public SaveEncounter(encounter: Encounter, then: () => void): void {
        const requestData = {
            Encounter: encounter
        };

        this.dispatch(ServerOperations.Server.Myle_Patient_MedicalNote_SaveEncounter, requestData, then);
    }

    public SaveAsNewEncounter(encounter: Encounter): void {
        // first make a call to fetch the current state of eht encounter, so it can be refreshed in summary list
        const requestDataReload =
        {
            EncounterId: encounter.Id
        };

        this.Execute(ServerOperations.Server.Myle_Patient_MedicalNote_LoadEncounter, requestDataReload, SubServerOperations.MedicalNote.ForSummaryRefresh);

        // then we do the call to save as
        const requestData =
        {
            Encounter: encounter
        };

        this.Execute(ServerOperations.Server.Myle_Patient_MedicalNote_SaveAsNewEncounter, requestData);
    }

    public GetEncounterStatus(encounterId: string, callerIdentifier: string, patientContactId: string, then: () => void): void {
        let requestData: IMyle_Patient_MedicalNote_GetStatusRequest = {
            EncounterId: encounterId,
            PatientContactId: patientContactId != null ? patientContactId : null,
            callerIdentifier // used by frontEnd Response Handler
        };

        this.dispatch(ServerOperations.Server.Myle_Patient_MedicalNote_GetStatus, requestData, then);
    }

    public SaveFreeFormImage(encounterId: string, base64ImageString: string, position: number, updateImageId?: string): void {
        const requestData = {
            EncounterId: encounterId,
            ImageId: null,
            Base64Image: base64ImageString,
            Position: position
        };

        if (updateImageId != null) {
            requestData.ImageId = updateImageId;
        }

        this.Execute(ServerOperations.Server.Myle_SaveHandNoteFile, requestData);
    }

    public DeleteFreeFormImage(encounterId: string, imageId: string): void {
        const requestData = {
            EncounterId: encounterId,
            ImageId: imageId
        };

        this.Execute(ServerOperations.Server.Myle_DeleteHandNoteFile, requestData);
    }

    public SignoffEncounter(encounterId: string, consultationTypeId: string, additionalSignOffInformation: AdditionalSignOffInformation, onSuccess?: () => void, onError?: () => void): void {
        const requestData = {
            EncounterId: encounterId,
            ConsultationTypeId: consultationTypeId,
            appointmentId: additionalSignOffInformation.appointmentId,
            InterfaceNoteCategory: additionalSignOffInformation.interfaceNoteCategory
        };

        this.Execute(
            ServerOperations.Server.Myle_Patient_MedicalNote_SignoffEncounter,
            requestData,
            null,
            null,
            () => {
                additionalSignOffInformation?.ValidationCallback?.(false);
                onError?.();
            },
            () => {
                additionalSignOffInformation?.ValidationCallback?.(true);
                onSuccess?.();
            }
        );
    }

    public CreatePdfForm(
        language: string,
        formId: string,
        formName: string,
        patientModel: Patient,
        employeeModel: Employee,
        encounterModel: IPatientMedicalNoteModel,
        summaryModel: IPatientMedicalSummaryModel,
        siteResponseModel: SitesModel
    ): void {
        const encounter = encounterModel.get("Encounter");

        const vitalSigns = summaryModel.get("MedicalItemsVitalSign").toArray();

        let currentVitalSigns = null;

        let vitalSignDTO = {};
        let lastVitalSignsWeight: number;
        let lastVitalSignsDate: string;
        let lastWeightUnit = null;
        if (vitalSigns && vitalSigns.length > 0) {
            currentVitalSigns = vitalSigns.filter(x => x.RelatedEncounterId == encounterModel.get("Encounter").Id && !x.EMRTemplateIsInvalid).sortByPropertyDesc(x => x.CommitDate).firstOrDefault() ?? null;
            let lastVitalSigns = vitalSigns.filter(x => !x.EMRTemplateIsInvalid).sortByPropertyDesc(x => x.CommitDate)[0] ?? null;
            if (lastVitalSigns != null) {
                lastVitalSignsWeight = lastVitalSigns?.Weight;
                lastVitalSignsDate = moment(lastVitalSigns?.PharmaStartDate).format("YYYY-MM-DD");
                lastWeightUnit = cacheStore.GetLookupById(lastVitalSigns?.WeightUnitLookupId);
                vitalSignDTO = {
                    LastVitalSignsWeight: lastVitalSignsWeight,
                    LastVitalSignsDate: lastVitalSignsDate ?? null,
                    LastWeightUnit: (lastVitalSignsWeight != null) ? lastWeightUnit?.GetValue() : ""
                };
            }
        }

        if (currentVitalSigns != null) {
            let TemperatureType = cacheStore.GetLookupById(currentVitalSigns.TemperatureTypeLookupId);
            let TemperatureUnit = cacheStore.GetLookupById(currentVitalSigns.TemperatureUnitLookupId);
            let WeightUnit = cacheStore.GetLookupById(currentVitalSigns.WeightUnitLookupId);
            let HeightUnit = cacheStore.GetLookupById(currentVitalSigns.HeightUnitLookupId);
            let WaistCircumferenceUnit = cacheStore.GetLookupById(currentVitalSigns.WaistCircumferenceUnitLookupId);
            let HeadCircumferenceUnit = cacheStore.GetLookupById(currentVitalSigns.HeightUnitLookupId);
            let BloodGlucoseUnit = cacheStore.GetLookupById(currentVitalSigns.BloodGlucoseUnitLookupId);

            vitalSignDTO = {
                Temperature: currentVitalSigns.Temperature,
                TemperatureType: (currentVitalSigns.Temperature != null) ? TemperatureType?.GetValue() : "",
                TemperatureUnit: (currentVitalSigns.Temperature != null) ? TemperatureUnit?.GetValue() : "",
                BloodPressureSystolicSitting: currentVitalSigns.BPSys,
                BloodPressureDiastolicSitting: currentVitalSigns.BPDia,
                BloodPressureSystolicStanding: currentVitalSigns.BPSysStanding,
                BloodPressureDiastolicStanding: currentVitalSigns.BPDiaStanding,
                BloodPressureSystolicLying: currentVitalSigns.BPSysLaid,
                BloodPressureDiastolicLying: currentVitalSigns.BPDiaLaid,
                HeartRate: currentVitalSigns.HeartRate,
                HeartRateIsRegular: currentVitalSigns.IsRegular,
                O2Saturation: currentVitalSigns.O2Sat,
                RespiratoryRate: currentVitalSigns.RespiratoryRate,
                MassBodyIndex: currentVitalSigns.MassBodyIndex?.toFixed(2),
                Weight: currentVitalSigns.Weight,
                WeightUnit: (currentVitalSigns.Weight != null) ? WeightUnit?.GetValue() : "",
                LastVitalSignsWeight: lastVitalSignsWeight,
                LastVitalSignsDate: lastVitalSignsDate,
                LastWeightUnit: (lastVitalSignsWeight != null) ? lastWeightUnit?.GetValue() : "",
                Height: currentVitalSigns.Height,
                HeightUnit: (currentVitalSigns.Height != null) ? HeightUnit?.GetValue() : "",
                WaistCircumference: currentVitalSigns.WaistCircumference,
                WaistCircumferenceUnit: (currentVitalSigns.WaistCircumference != null) ? WaistCircumferenceUnit?.GetValue() : "",
                HeadCircumference: currentVitalSigns.HeadCircumference,
                HeadCircumferenceUnit: (currentVitalSigns.HeadCircumference != null) ? HeadCircumferenceUnit?.GetValue() : "",
                SerumCreatinine: currentVitalSigns.SerumCreatinine,
                SerumCreatinineUnit: currentVitalSigns.SerumCreatinine != null ? currentVitalSigns.CreatinineUnit : "",
                CreatinineClearance: currentVitalSigns.CreatinineClearance,
                BloodGlucose: currentVitalSigns.BloodGlucose,
                BloodGlucoseUnit: (currentVitalSigns.BloodGlucose != null) ? BloodGlucoseUnit?.GetValue() : "",
                BodySurface: currentVitalSigns.BodySurface?.toFixed(2),
                Note: currentVitalSigns.Note
            };
        }

        // Employee
        const licence = (employeeModel.Licences != null && employeeModel.Licences.length > 0) ? employeeModel.Licences[0].LicenceNumber : "";

        // Employee Fax
        let clinicPhone: string = "", clinicFax: string = "", clinicPhoneExt: string = "";
        if (siteResponseModel.Model.Phones?.length) {
            let clinicPhoneDetails = cacheStore.GetPhoneDetailsByType(siteResponseModel.Model.Phones, "PHONE", null);
            clinicPhone = clinicPhoneDetails?.number || "";
            clinicPhoneExt = clinicPhoneDetails?.extension || "";
            clinicFax = cacheStore.GetPhoneByType(siteResponseModel.Model.Phones, "FAX", null) || "";
        }
        // code Sil-p
        const codeSilp = siteResponseModel.Model.SiteIdentifiers?.filter(s => s.IdentifierTypeCode === "CA.QC.COSIL.SourceId").firstOrDefault()?.Value;
        const address = siteResponseModel.Model.Adresses.firstOrDefault();
        const stateLookup = cacheStore.GetLookupById(address?.StateLookupId);

        let attendingPhysician = patientModel.GetAttendingDoctor();
        let otherAttendingPhysician: PatientRefIntervenorWithDetailsModel = patientModel
            .GetAttendingDoctorOrSpecialistOnfileIntervenor(sessionStore.getCurrentEmployee()?.ContactFacetId);

        const EmployeeDTO = {
            ClinicName: siteResponseModel.Model.ClinicalName,
            EmployeeId: employeeModel.ContactFacet_id,
            Firstname: employeeModel.FirstName,
            Lastname: employeeModel.LastName,
            PermitNumber: licence,
            datevisite: MyleHelper.GetStandardizedDateString(encounter.ConsultationDate),
            AddressDTO: {
                StreetAddress: address?.StreetAddress,
                City: address?.City,
                PostalCode: address?.ZipCode,
                ProvinceFullName: stateLookup?.GetValue(),
                ProvinceCode: stateLookup?.LookupCode?.split("_")?.reverse()?.firstOrDefault(), // CanadaState_ON => ON
                Province: stateLookup?.LookupCode?.split("_")?.reverse()?.firstOrDefault(), // Legacy Province use fullName
                Country: cacheStore.GetLookupById(address?.CountryLookupId)?.GetValue(),
                CountryCode: cacheStore.getCountryByLookupId(address?.CountryLookupId)?.Alpha2Code
            },
            Phone: clinicPhone,
            PhoneEXT: clinicPhoneExt ?? "",
            Fax: clinicFax,
            University: employeeModel.University,
            PractitionerNumber: employeeModel?.ProfessionalNumber,
            Prefix: cacheStore.GetLookupById(employeeModel.PrefixLookupId)?.GetValue(),
            Title: LicenceOrganisationRule.getTitleName(Employee.getIssuingOrgRule(employeeModel)),
            Specialty: cacheStore.GetLookupById(employeeModel.SpecialtyLookupId)?.GetValue(),
            ClinicCode: siteResponseModel.Model.ClinicalCode,
            ClinicEmail: siteResponseModel.Model.Emails?.firstOrDefault(s => s.IsPreferred)?.Address ?? siteResponseModel.Model.Emails?.firstOrDefault()?.Address,
            SiteCodeSilp: codeSilp ?? ""
        };

        const patientDob = patientModel.IndividualResponse.DateOfBirth == null ? null : moment.utc(patientModel.IndividualResponse.DateOfBirth).format("YYYYMMDD");
        const patientDobMDYWithSlash = patientModel.IndividualResponse.DateOfBirth == null ? null : moment.utc(patientModel.IndividualResponse.DateOfBirth).format("MM/DD/YYYY");

        let motherName = patientModel.IndividualResponse?.MotherLastName
            ? `${patientModel.IndividualResponse.MotherLastName}, ${patientModel.IndividualResponse?.MotherFirstName ?? ""}`
            : `${patientModel.IndividualResponse?.MotherFirstName ?? ""}`;

        let fatherName = patientModel.IndividualResponse?.FatherLastName
            ? `${patientModel.IndividualResponse.FatherLastName}, ${patientModel.IndividualResponse?.FatherFirstName ?? ""}`
            : `${patientModel.IndividualResponse?.FatherFirstName ?? ""}`;

        let tmpNAM_Expiration_Year = patientModel.GetFormattedNAMExpirationDateString("YYYY");
        let tmpNAM_Expiration_Month = patientModel.GetFormattedNAMExpirationDateString("MM");

        let email = "";
        if (patientModel.Emails != null && patientModel.Emails.length > 0)
            email = patientModel.Emails[0].Address;

        let CellHomeWorkList = [];
        let HomeCellWorkList = [];
        let WorkCellHomeList = [];
        let preferredPhone = "";
        let personalHome, personalCell, work = "";

        if (patientModel.Phones != null && patientModel.Phones.length > 0) {
            personalHome = cacheStore.GetPhoneByType(patientModel.Phones, "PHONE", "PERSONAL");
            personalCell = cacheStore.GetPhoneByType(patientModel.Phones, "MOBILE", "PERSONAL");
            work = cacheStore.GetPhoneByType(patientModel.Phones, null, "WORK");

            if (personalCell != null && personalCell != "")
                CellHomeWorkList.push(personalCell);
            if (personalHome != null && personalHome != "")
                CellHomeWorkList.push(personalHome);
            if (work != null && work != "")
                CellHomeWorkList.push(work);

            if (personalHome != null && personalHome != "")
                HomeCellWorkList.push(personalHome);
            if (personalCell != null && personalCell != "")
                HomeCellWorkList.push(personalCell);
            if (work != null && work != "")
                HomeCellWorkList.push(work);

            if (work != null && work != "")
                WorkCellHomeList.push(work);
            if (personalCell != null && personalCell != "")
                WorkCellHomeList.push(personalCell);
            if (personalHome != null && personalHome != "")
                WorkCellHomeList.push(personalHome);

            preferredPhone = patientModel.Phones.firstOrDefault(p => p.IsPreferred)?.Number ?? "";
        }

        let CombinedPhoneView1_CellHomeWork = CellHomeWorkList.length > 0 ? CellHomeWorkList[0] : "";
        let CombinedPhoneView2_CellHomeWork = CellHomeWorkList.length > 1 ? CellHomeWorkList[1] : "";
        let CombinedPhoneView3_CellHomeWork = CellHomeWorkList.length > 2 ? CellHomeWorkList[2] : "";

        let CombinedPhoneView1_HomeCellWork = HomeCellWorkList.length > 0 ? HomeCellWorkList[0] : "";
        let CombinedPhoneView2_HomeCellWork = HomeCellWorkList.length > 1 ? HomeCellWorkList[1] : "";
        let CombinedPhoneView3_HomeCellWork = HomeCellWorkList.length > 2 ? HomeCellWorkList[2] : "";

        let CombinedPhoneView1_WorkCellHome = WorkCellHomeList.length > 0 ? WorkCellHomeList[0] : "";
        let CombinedPhoneView2_WorkCellHome = WorkCellHomeList.length > 1 ? WorkCellHomeList[1] : "";
        let CombinedPhoneView3_WorkCellHome = WorkCellHomeList.length > 2 ? WorkCellHomeList[2] : "";

        let gender = "";
        let sexM: boolean, sexF: boolean = false;
        if (patientModel.Gender() === Gender.Female) {
            gender = "F";
            sexF = true;
        }

        if (patientModel.Gender() === Gender.Male) {
            gender = "M";
            sexM = true;
        }
        const familyDoctor: PatientRefIntervenorWithDetailsModel = patientModel.GetAttendingDoctorOrSpecialistOnfileIntervenor();
        const externalConsultant: PatientExternalIdentifier = patientModel.GetVisibleExternalIdentifersForSiteId(encounter.SiteId).firstOrDefault();
        const ipsIntervenor: PatientRefIntervenorWithDetailsModel = patientModel.GetIpsIntervenorOnFile();
        const patientAddress = patientModel.Addresses.firstPreferredOrDefault(address => address.IsPreferred) ?? patientModel.Addresses.firstOrDefault();
        const pharmacyInfo = patientModel.PreferredPharmacy.getPharmacyInfo();

        const problemsOnGoing: MedicalItemProblemAndCondition[] = summaryModel.get("MedicalItemsProblemsOnGoing")?.toArray() ?? [];
        const activeTreatments = problemsOnGoing
            .filter(problem => problem.EMRTemplateIsVisibleInCPP && !problem.EMRTemplateIsInvalid && problem.ForceDisplayInMedicalHistory)
            .sortByPropertyDesc(p => p.CommitDate)
            .map(problem => problem.QualifiedDetails.map(detail => detail.Text).toString());

        const genderIdentityName = EnumHelper.getName(GenderIdentity, patientModel.IndividualResponse.GenderIdentity);
        const genderIdentityVocabKey = `Enums_GenderIdentity_${genderIdentityName}`;
        const preferredPronounsName = EnumHelper.getName(PreferredPronouns, patientModel.IndividualResponse.PreferredPronouns);
        const preferredPronounsVocabKey = `Enums_PreferredPronouns_${preferredPronounsName}`;

        const patientHealthCardDetails = patientModel.HealthCards?.[0];
        const isHealthCardFromOntario = patientHealthCardDetails?.ProvinceLookupId == cacheStore.FindLookupByLookupCode(CanadaState.Ontario).LookupID;

        let nam = patientModel.IndividualResponse.NAM;
        let namVersion = "";

        if (isHealthCardFromOntario) {
            const result = /^([1-9]\d{9})-?([A-Za-z]{0,2})$/.exec(patientHealthCardDetails.Number);
            if (result) {
                nam = result[1];
                namVersion = result[2];
            }
        }

        const PatientDTO = {
            Gender: gender,
            GenderIdentity: cacheStore.HasString(genderIdentityVocabKey) ? cacheStore.GetString(genderIdentityVocabKey) : genderIdentityName,
            SexM: sexM,
            SexF: sexF,
            Firstname: patientModel.IndividualResponse.FirstName,
            Lastname: patientModel.IndividualResponse.LastName,
            PreferredName: patientModel.IndividualResponse.PreferredName,
            BirthDate: patientDob,
            BirthDateMDYWithSlash: patientDobMDYWithSlash,
            NAM: nam,
            FileNumber: patientModel.PatientResponse.Identifier01,
            identifier01: patientModel.PatientResponse.Identifier01,
            identifier02: patientModel.PatientResponse.Identifier02,
            identifier03: patientModel.PatientResponse.Identifier03,
            Age: patientModel.GetFormattedAge(),
            NAM_Expiration_Year: tmpNAM_Expiration_Year,
            NAM_Expiration_Month: tmpNAM_Expiration_Month,
            NAM_Version: namVersion,
            MotherName: motherName,
            MotherFirstName: patientModel.IndividualResponse.MotherFirstName,
            MotherLastName: patientModel.IndividualResponse.MotherLastName,
            FatherFirstName: patientModel.IndividualResponse.FatherFirstName,
            FatherLastName: patientModel.IndividualResponse.FatherLastName,
            FatherName: fatherName,
            PreferredPronoun: cacheStore.HasString(preferredPronounsVocabKey) ? cacheStore.GetString(preferredPronounsVocabKey) : preferredPronounsName,

            PharmacyBanner: pharmacyInfo.Banner,
            PharmacyName: pharmacyInfo.Name,
            PharmacyPhone: pharmacyInfo.Phone,
            PharmacyFax: pharmacyInfo.Fax,
            PharmacyAddress: pharmacyInfo.Address,

            Email: email,
            EmailPerso: email,

            PersonalHomePhone: personalHome,
            PersonalCell: personalCell,
            WorkPhone: work,
            PreferredPhoneNumber: preferredPhone,
            CombinedPhoneView1_CellHomeWork: CombinedPhoneView1_CellHomeWork,
            CombinedPhoneView2_CellHomeWork: CombinedPhoneView2_CellHomeWork,
            CombinedPhoneView3_CellHomeWork: CombinedPhoneView3_CellHomeWork,

            CombinedPhoneView1_HomeCellWork: CombinedPhoneView1_HomeCellWork,
            CombinedPhoneView2_HomeCellWork: CombinedPhoneView2_HomeCellWork,
            CombinedPhoneView3_HomeCellWork: CombinedPhoneView3_HomeCellWork,

            CombinedPhoneView1_WorkCellHomeList: CombinedPhoneView1_WorkCellHome,
            CombinedPhoneView2_WorkCellHomeList: CombinedPhoneView2_WorkCellHome,
            CombinedPhoneView3_WorkCellHomeList: CombinedPhoneView3_WorkCellHome,
            dayphone: CombinedPhoneView1_CellHomeWork,
            DateNow_MDY_Slash: moment().format("MM/DD/YYYY"),

            AddressDTO: {
                StreetAddress: patientAddress?.StreetAddress,
                City: patientAddress?.City,
                PostalCode: patientAddress?.ZipCode,
                ProvinceFullName: cacheStore.GetLookupById(patientAddress?.StateLookupId)?.GetValue(),
                ProvinceCode: cacheStore.GetLookupById(patientAddress?.StateLookupId)?.LookupCode?.split("_")?.reverse()?.firstOrDefault(), // CanadaState_ON => ON
                Province: cacheStore.GetLookupById(patientAddress?.StateLookupId)?.GetValue(), // Legacy patient uses province full name
                Country: cacheStore.GetLookupById(patientAddress?.CountryLookupId)?.GetValue(),
                CountryCode: cacheStore.getCountryByLookupId(patientAddress?.CountryLookupId)?.Alpha2Code
            },

            Employer: patientModel.PatientResponse.Employer,
            PatientStatusDate: patientModel.PatientResponse.PatientStatusDate != null ? moment(patientModel.PatientResponse.PatientStatusDate).format("YYYY/MM/DD") : "",
            SocialNumber: patientModel.PatientResponse.SocialNumber,
            NextAppointmentDate: (patientModel.UpcomingAppointment != null) ? moment(patientModel.UpcomingAppointment.DateTime).format("YYYY/MM/DD - HH:mm") : "",
            NextAppointmentSubject: (patientModel.UpcomingAppointment != null) ? cacheStore.GetAppointmentDescription(patientModel.UpcomingAppointment.Subject, patientModel.UpcomingAppointment.ServiceDefinitionId) : "",
            InsuranceCompany: patientModel.InsuranceCompany?.Provider,
            FamilyDoctor: familyDoctor?.Model?.Intervenor?.IsPrimary ? familyDoctor.GetName() : "",
            ExternalConsultant: externalConsultant?.ResponseModel?.Number,
            Nurse: ipsIntervenor?.Model?.Intervenor?.IsPrimary ? ipsIntervenor.GetName() : "",
            VulnerabilityCodes: patientModel.RAMQInfo?.RAMQInscription?.CaracteristiqueSantes?.map(cs => cs.CodeSante).join("; "),
            ActiveTreatments: activeTreatments,

            AttendingPhysician: attendingPhysician?.FullName ?? "",
            OtherAttendingPhysician: otherAttendingPhysician?.Employee?.nameWithPrefix() ?? "",
            OtherAttendingPhysicianLicenceOrProfessionnalNumber: otherAttendingPhysician?.Employee?.getLicenseDtos()?.firstOrDefault()?.LicenseNumber ?? ""
        };

        const FillingDto = {
            EmployeeDTO: EmployeeDTO,
            PatientDTO: PatientDTO,
            VitalSignDTO: vitalSignDTO
        };

        const FormRequest = {
            FormId: formId,
            FormName: formName,
            Culture: language,
            EncounterId: encounter.Id,
            EncounterCreateDate: encounter.ConsultationDate,
            FillingDto: FillingDto
        };

        const requestModel = { FormRequest };
        this.Execute(ServerOperations.Server.Myle_Patient_MedicalNote_CreateNewForm, requestModel);
    }

    public DeleteRadPdf(patientFormId: string, encounterId: string): void {
        var requestModel =
        {
            PatientFormId: patientFormId,
            EncounterId: encounterId
        };

        this.Execute(ServerOperations.Server.Myle_Patient_MedicalNote_DeleteForm, requestModel);
    }

    public PatientFormIsPersistant(encounterId: Guid, patientFormId: Guid, isPersistant: boolean): void {
        var requestModel: IMyle_Patient_MedicalNote_GetFormPersistRequest =
        {
            EncounterId: encounterId,
            PatientFormId: patientFormId,
            IsPersistant: isPersistant
        };

        this.Execute(ServerOperations.Server.Myle_Patient_MedicalNote_IsPersistant, requestModel);
    }

    public generatePrescriptionPdf(prescriptionId: string, withSignature: boolean, forceNonThermicPrintMode: boolean, pharmacy: Pharmacy, then: (fileName: string) => void): void {
        this.dispatch(
            ServerOperations.Server.Myle_Patient_GeneratePrescriptionPDF,
            {
                PrescriptionId: prescriptionId,
                WithSignature: withSignature,
                FaxRxComposite: Pharmacy.getFaxComposite(pharmacy),
                ForceNonThermicPrintMode: forceNonThermicPrintMode,
                IsForQuebec: sessionStore.getCurrentEmployee().isActiveSiteQuebec(),
                SupervisorContactId: sessionStore.lockedSupervisorContactId
            },
            then
        );
    }

    public GenerateNotePDF(encounterId: string, withSignature: boolean, patientContactId: string, isPrintingFromApprovedEncounter: boolean): void {
        var request = {
            PatientContactId: patientContactId,
            EncounterId: encounterId,
            WithSignature: withSignature,
            ShowDiagnosisCode: settingsStore.configuration.get("ShowDiagnosisCode"),
            IsPrintingFromApprovedEncounter: isPrintingFromApprovedEncounter
        };

        this.Execute(ServerOperations.Server.Myle_Patient_MedicalNote_GenerateNotePDF, request);
    }

    public SavePrivateBill(privateBillModel: IPatientPrivateBillModel, billedServices: IBillableService[], patientId: string, createdById: string, encounterId: string, isClosingBill: boolean = false): void {
        var provider = privateBillModel.get("SelectedClinicians")?.toArray();
        var discountType = privateBillModel.get("IsCashSelected") === true ? 0 : 1;

        if (isClosingBill == null) isClosingBill = false;

        var requestData = {
            DateOfVisit: MyleHelper.GetStandardizedDateString(privateBillModel.get("SelectedDateOfVisit")),
            SiteId: privateBillModel.get("SelectedSiteId"),
            ProviderId: provider?.[0]?.ContactFacetId,
            CreatedById: createdById,
            PatientId: patientId,
            PaymentMethod: Number(privateBillModel.get("SelectedPaymentMethod")),
            DiscountType: discountType,
            GSTPercent: privateBillModel.get("GSTPercent"),
            QSTPercent: privateBillModel.get("QSTPercent"),
            HSTPercent: privateBillModel.get("HSTPercent"),
            GenericTaxPercent: privateBillModel.get("GenericTaxPercent"),
            Discount: privateBillModel.get("NewBillDiscount"),
            TotalGST: privateBillModel.get("NewBillGSTTotal"),
            TotalQST: privateBillModel.get("NewBillQSTTotal"),
            TotalHST: privateBillModel.get("NewBillHSTTotal"),
            TotalGenericTax: privateBillModel.get("NewBillGenericTaxTotal"),
            Total: privateBillModel.get("NewBillTotal"),
            Notes: privateBillModel.get("NewBillNotes"),
            BilledServices: [],
            EncounterId: encounterId,
            IsFromClose: isClosingBill,
            IsFromEdition: privateBillModel.get("IsEditBill"),
            PrivateBillId: privateBillModel.get("PrivateBillId"),
            RecipientName: "",
            RecipientAddress: "",
            RecipientPhoneNumber: "",
            RecipientFaxNumber: "",
            BilledTo: Number(PrivateBill_BilledTo.Patient)
        };

        if (cacheStore.ClinicSettings.EnablePrivateBillRecipient) {
            const recipientDetails = privateBillModel.get("SelectedBillRecipientDetails");

            requestData.RecipientName = recipientDetails.RecipientName;
            requestData.RecipientAddress = recipientDetails.RecipientAddress;
            requestData.RecipientPhoneNumber = recipientDetails.RecipientPhoneNumber;
            requestData.RecipientFaxNumber = recipientDetails.RecipientFaxNumber;
            requestData.BilledTo = privateBillModel.get("SelectedBillBilledTo");
        }

        for (let service of billedServices) {
            if (service.get("SelectedService") == null)
                continue;

            let billData = {
                Qty: service.get("Qty"),
                Price: service.get("Price"),
                ServiceNumber: service.get("ServiceNumber"),
                NameFr: service.get("NameFr"),
                NameEn: service.get("NameEn"),
                NameEs: service.get("NameEs"),
                GST: service.get("GST"),
                QST: service.get("QST"),
                HST: service.get("HST"),
                GenericTax: service.get("GenericTax"),
                HadGST: service.get("HasGST") == true ? true : false,
                HadQST: service.get("HasQST") == true ? true : false,
                HadHST: service.get("HasHST") == true ? true : false,
                HadGenericTax: service.get("HasGenericTax") == true ? true : false,
                CostCenterId: service.get("SelectedService").Service.CostCenterId
            };

            requestData.BilledServices.push(billData);
        }

        this.Execute(ServerOperations.Server.Myle_Patient_History_SavePrivateBill, requestData);
    }

    public DeleteMedfarBill(billId: string, encounterId: string): void {
        const requestData = {
            BillId: billId,
            EncounterId: encounterId
        };

        this.Execute(ServerOperations.Server.MedfarBilling_DeleteBill, requestData);
    }

    public PrintPrivateBill(privateBillId: string, isFromPrint: boolean): void {
        var requestData = {
            PrivateBillId: privateBillId
        };

        this.Execute(ServerOperations.Server.Myle_Patient_History_GeneratePatientPrivateBillPDF, requestData, isFromPrint === true ? SubServerOperations.MedicalNote.GeneratePrivateBillForPrint : SubServerOperations.MedicalNote.GeneratePrivateBillForReuse);
    }

    public InvalidatePrivateBill(privateBillId: string, invalidationNote: string): void {
        var requestData = {
            PrivateBillId: privateBillId,
            InvalidationNote: invalidationNote,
            EmployeeId: sessionStore.getCurrentEmployee().ContactFacetId
        };

        this.Execute(ServerOperations.Server.Myle_Patient_History_InvalidatePrivateBill, requestData);
    }

    public LoadCostCenterForSite(siteId: string): void {
        var requestData = {
            SiteId: siteId
        };

        this.Execute(ServerOperations.Server.Myle_Patient_History_PrivateBill_LoadCostCenterForMexico, requestData);
    }

    public SendPendingApproval(supervisorContactId: string, encounterId: string, message: string, AllowList: IAllowlistModel): void {
        let residentContactId = sessionStore.getCurrentEmployee().ContactId;

        var requestData = {
            SupervisorContactId: supervisorContactId,
            ResidentContactId: residentContactId,
            EncounterId: encounterId,
            Message: message,
            AllowListChanges: AllowList
        };

        this.Execute(ServerOperations.Server.Myle_Resident_SendPendingApproval, requestData);
    }

    public LoadPendingApprovalStatus(encounterId: string): void {
        var requestData = {
            EncounterId: encounterId
        };

        this.Execute(ServerOperations.Server.Myle_Resident_LoadPendingStatus, requestData);
    }

    public PrintConsultationRequest(request: IPrintConsultationRequest, then?: (fileName: string) => void): void {
        let requestData = {
            ConsultationRequestId: request.RequestId,
            WithSignature: request.WithSignature,
            MarkAsPrinted: request.MarkAsPrinted
        };
        this.dispatch(ServerOperations.Server.Myle_PrintConsultationRequest, requestData, then);
    }

    public PrintSelectedRequest(request: IPrintLabRequest, then?: (fileName: string) => void): void {
        let requestData = {
            RequestId: request.RequestId,
            AddSignature: request.AddSignature,
            FaxLabRequestComposite: request.FaxLabRequestComposite,
            MarkAsPrinted: request.MarkAsPrinted
        };
        this.dispatch(ServerOperations.Server.Myle_PrintSelectedRequest, requestData, then);
    }

    public SendLabRequestFax(faxLabRequestComposite: FaxLabRequestComposite, then?: () => void): void {
        var requestData = {
            FaxLabRequestComposite: faxLabRequestComposite
        };
        this.dispatch(ServerOperations.Server.Myle_Faxing_SendLabRequest, requestData, then);
    }

    public FaxConsultationRequest(faxConsultationRequestComposite: FaxConsultationRequestComposite, then?: () => void): void {
        var requestData = {
            FaxConsultationRequestComposite: faxConsultationRequestComposite
        };

        this.dispatch(ServerOperations.Server.Myle_Faxing_SendConsultationRequest, requestData, then, false, SubServerOperations.MedicalNote.FaxConsultationRequest);
    }

    public async AttachAPSSFiles(requestId: string, documentCode: string, attachments: ExportableFile[], patientContactId?: string): Promise<void> {
        const { token } = await sessionStore.getAPSSToken();
        if (!token)
            return;

        this.Execute(
            ServerOperations.Server.Myle_UploadApssAttachments,
            {
                ApssRequestId: requestId,
                AttachmentCode: documentCode,
                ApssCookie: token,
                Attachments: attachments.map(x => x.toAttachedObject()),
                PatientContactId: patientContactId
            }
        );
    }

    public LoadPrivateBillList(patientId: string, searchText: string, dateFrom: Date, dateTo: Date, isFromReuse: boolean, encounterId: string = ""): void {
        var requestData = {
            PatientId: patientId,
            SearchText: searchText,
            DateFrom: dateFrom ? MyleHelper.GetStandardizedDateString(dateFrom) : null,
            DateTo: dateTo ? MyleHelper.GetStandardizedDateString(dateTo) : null,
            EncounterId: encounterId
        };

        this.Execute(ServerOperations.Server.Myle_PrivateBill_LoadLightPrivateBill, requestData, isFromReuse === true ? SubServerOperations.PrivateBill.LoadListForReuse : SubServerOperations.PrivateBill.LoadListForEncounter);
    }

    public generateChartsPDF(
        patientContactId: string,
        isHeadCircumferenceChecked_vitalsGraph: boolean,
        isWeightChecked_vitalsGraph: boolean,
        isHeightChecked_vitalsGraph: boolean,
        isBmiChecked_vitalsGraph: boolean,
        isHeadCircumferenceChecked_gc: boolean,
        IsWeightChecked0To2_gc: boolean,
        IsWeightChecked2Plus_gc: boolean,
        isHeightChecked_gc: boolean,
        isBmiChecked0To2_gc: boolean,
        isBmiChecked2Plus_gc: boolean,
        isLengthChecked_gc: boolean,
        unitSystem: UnitSystem,
        versionChart?: GrowthChartStandard
    ): void {
        var request = {
            PatientContactId: patientContactId,
            IsHeadCircumferenceChecked_vitalsGraph: isHeadCircumferenceChecked_vitalsGraph,
            IsWeightChecked_vitalsGraph: isWeightChecked_vitalsGraph,
            IsHeightChecked_vitalsGraph: isHeightChecked_vitalsGraph,
            IsBmiChecked_vitalsGraph: isBmiChecked_vitalsGraph,
            IsHeadCircumferenceChecked_growthCurves: isHeadCircumferenceChecked_gc,
            IsWeightChecked0To2_growthCurves: IsWeightChecked0To2_gc,
            IsWeightChecked2Plus_growthCurves: IsWeightChecked2Plus_gc,
            IsHeightChecked_growthCurves: isHeightChecked_gc,
            IsLengthChecked_growthCurves: isLengthChecked_gc,
            IsBmiChecked0To2_growthCurves: isBmiChecked0To2_gc,
            IsBmiChecked2Plus_growthCurves: isBmiChecked2Plus_gc,
            UnitSystem: unitSystem,
            VersionChart: versionChart
        };

        this.Execute(ServerOperations.Server.Myle_Patient_Merged_GrowthCurvesAndVitalsPDF, request);
    }

    public unapproveEncounter(encounterId: string, patientId: string): void {
        var requestData = {
            EncounterId: encounterId,
            PatientId: patientId
        };
        this.Execute(ServerOperations.Server.Myle_UnapproveMedicalEncounter, requestData);
    }

    public deleteEncounter(encounterId: string, patientId: string, reasonCode: string, reasondetail: string): void {
        var requestData = {
            EncounterId: encounterId,
            PatientId: patientId,
            ReasonCode: reasonCode,
            ReasonDetail: reasondetail
        };
        this.Execute(ServerOperations.Server.Myle_DeleteMedicalEncounter, requestData);
    }

    public loadAttachedCommunications(encounterId: string): void {
        this.Execute(
            ServerOperations.Server.Myle_LoadAttachedCommunications,
            { EncounterId: encounterId },
            SubServerOperations.AttachedCommunications.Encounter
        );
    }

    public sendRequestToPortal(requestData: any): void {
        this.Execute(
            ServerOperations.Server.Myle_Patient_Portal_SyncSharedItem,
            requestData,
            SubServerOperations.PortalLinkedItemStatus.Requests
        );
    }

    public revokeSharedItem(data: any, then?: () => void): void {
        this.dispatch(
            ServerOperations.Server.Myle_Patient_Portal_RevokeSharedItem,
            data,
            then,
            false,
            SubServerOperations.PortalRevokeItem.FromMedicalNote
        );
    }

    public reloadSharedItem(sharedItemId: string, then: (sharedItem: SharedItem) => void): void {
        this.dispatch(
            ServerOperations.Server.Myle_Patient_Portal_GetSharedItem,
            {
                SharedItemId: sharedItemId,
                IncludeLinkedItem: true
            },
            then
        );
    }

    public sendMedicalFormToPortal(requestData: any): void {
        this.Execute(
            ServerOperations.Server.Myle_Patient_Portal_SyncSharedItem,
            requestData,
            SubServerOperations.PortalLinkedItemStatus.MedicalNoteForm
        );
    }

    public async copySignedPdfToPortal(requestId: string, unsignedFileName: string, signedFileName: string, portalLinkedItemType: PortalLinkedItemType, encounterId: string): Promise<boolean> {
        // if no signed file name is passed, use the unsigned filename
        if (!signedFileName || signedFileName.length == 0)
            signedFileName = unsignedFileName;

        const response = await Client.postAsync(
            ServerOperations.Server.Myle_Patient_Portal_CopyPermanentPdfForPortal,
            {
                PermanentPdfType: portalLinkedItemType,
                PermanentPdfId: requestId,
                FileNameSigned: signedFileName,
                FileNameUnsigned: unsignedFileName,
                EncounterId: encounterId
            }
        );

        return response.isSuccess;
    }

    public async copySignedAttachmentToPortal(requestId: string, attachmentId: string, portalLinkedItemType: PortalLinkedItemType, encounterId: string): Promise<boolean> {
        if (!attachmentId)
            throw new Error("The attachment ID is required.");

        const response = await Client.postAsync(
            ServerOperations.Server.Myle_Patient_Portal_CopyPermanentPdfForPortal,
            {
                PermanentPdfType: portalLinkedItemType,
                PermanentPdfId: requestId,
                EncounterId: encounterId,
                AttachmentId: attachmentId
            }
        );

        return response.isSuccess;
    }

    public reloadEncounterForms(encounterId: string, patientContactId: string): void {
        this.Execute(
            ServerOperations.Server.Myle_Patient_MedicalNote_ReloadForms,
            {
                EncounterId: encounterId,
                PatientContactId: patientContactId
            }
        );
    }

    public createNewObstetricalFile(encounterId: string, obstetricalFileType: ObstetricalFileType, form: string): void {
        this.Execute(
            ServerOperations.Server.Myle_Patient_MedicalNote_NewObstetricalFile,
            {
                EncounterId: encounterId,
                ObstetricalFileType: obstetricalFileType,
                Form: form
            }
        );
    }

    public createObstetricalFileContribution(encounterId: string, previousObstetricalFileContributionId: string, obstetricalFileType: ObstetricalFileType, contributionType: ObstetricalFileContributionType): void {
        this.Execute(
            ServerOperations.Server.Myle_Patient_MedicalNote_ObstetricalFileContribution,
            {
                EncounterId: encounterId,
                PreviousObstetricalFileContributionId: previousObstetricalFileContributionId,
                ObstetricalFileType: obstetricalFileType,
                ContributionType: contributionType
            }
        );
    }

    public deleteObstetricalFile(encounterId: string, obstetricalFileContributionId: string): void {
        this.Execute(
            ServerOperations.Server.Myle_Patient_MedicalNote_DeleteObstetricalFile,
            {
                EncounterId: encounterId,
                ObstetricalFileContributionId: obstetricalFileContributionId
            }
        );
    }

    public loadWorkSafeBcForms(encounterId: string, patientContactId: string): void {
        this.Execute(
            ServerOperations.Server.WorkSafeBc_GetExistingFormList,
            {
                PatientContactId: patientContactId,
                EncounterId: encounterId
            }
        );
    }

    public LoadSignedAuthorizations(params: { prescriptonIds?: string[], requestIds?: string[], consultationRequestIds?: string[], patientFormIds?: string[], encounterId?: string }): void {
        const requestData = {
            PrescriptionIds: params.prescriptonIds,
            RequestIds: params.requestIds,
            ConsultationRequestIds: params.consultationRequestIds,
            PatientFormIds: params.patientFormIds,
            EncounterId: params.encounterId
        };

        this.Execute(ServerOperations.Server.Myle_GetSignedPrintAuthorization, requestData);
    }

    public async GeneratePatientBillingHistoryPdfAsync(
        patientContactFacetId: string,
        orderType: PatientBillingHistoryOrderType,
        sortDirection: SortDirection
    ): Promise<GeneratePatientBillingHistoryPdfResponse> {
        const clientResponse = await Client.postAsync<GeneratePatientBillingHistoryPdfResponse>(
            ServerOperations.Server.Myle_Patient_MedicalNote_GeneratePatientBillingHistoryPdf,
            {
                PatientContactFacetId: patientContactFacetId,
                OrderDirection: sortDirection,
                OrderType: orderType
            }
        );

        return clientResponse.data;
    }

    public async loadSentFaxes(sentFaxItemIds: string[]): Promise<void> {
        return await this.ExecuteAsync(
            ServerOperations.Server.Myle_Faxing_GetSentFaxesByItemIds,
            { SentFaxItemIds: sentFaxItemIds }
        );
    }

    public completeMedicalNoteLinkedAppointment(appointment: PatientAppointmentItem): void {
        this.Execute(ServerOperations.Server.Myle_UpdateAppointment, {
            AppointmentId: appointment.AppointmentId,
            Appointment: appointment,
            Source: EnumAppointmentSource.Myle_MedicalNote
        });
    }
}

interface IPrintConsultationRequest {
    RequestId: string;
    WithSignature: boolean;
    MarkAsPrinted: boolean;
    MarkAsFaxed: boolean;
}

interface IPrintLabRequest {
    RequestId: string;
    AddSignature: boolean;
    FaxLabRequestComposite: IFaxLabRequestComposite;
    MarkAsPrinted: boolean;
}

interface IFaxLabRequestComposite {
    RequestId: string;
    WithSignature: boolean;
    LabFax: string;
    LabName: string;
}
interface IFaxConsultationRequestComposite {
    RequestId: string;
    WithSignature: boolean;
}

enum AppointmentStatusChangeEventType {
    NewEncounter = 1,
    ModifyEncounter = 2,
    SignedOffEncounter = 3,
    OnPatientClose
}

export {
    AppointmentStatusChangeEventType,
    IFaxConsultationRequestComposite,
    IFaxLabRequestComposite,
    IPrintConsultationRequest,
    IPrintLabRequest,
    PatientMedicalNoteActions
};
